import React, { ReactNode, useEffect, useRef, useState, useMemo } from 'react'
import Icon from '../Icon2/Icon';
import Menu from '../Menu/Menu';
import { IMenu } from '../Menu/Menu';
import AsyncSelect, { LoadMoreArgsProps, LoadMoreOptReturnProps } from '../Select/AsyncSelect';
import Button from '../Button/Button';
import './TopNavBar.css';
import { API_MAP, isMac } from './utils';
import { NavLink, BrowserRouter } from 'react-router-dom';

export enum DropdownItemType {
    REDIRECT = 'REDIRECT',
    ACTION = 'ACTION',
    ASYNC_DROPDOWN = 'ASYNC_DROPDOWN',
    DROPDOWN = 'DROPDOWN'
}
export type NotificationOptionType = "tasks" | "etc" | null;
export interface NavBarItemProps {
    displayName: string;
    type: "REDIRECT" | "DROPDOWN" | "ACTION" | "ASYNC_DROPDOWN";
    action?: string;
    icon?: string;
    iconVersion?: string;
    dynamicMenuItem?: boolean;
    redirectUrl?: string;
    hotKey?: string;
    trialExpired?: boolean;
    testId?: string;
    isActive?: boolean;
    subMenu?: NavBarItemProps[];
    id?: string;
    activeStates?: string[];
    hasNotificationOption?: NotificationOptionType;
    notificationCount?: string;
    uiValidator?: string;
    hookId?: string;
    preventRedirectIfActive?: boolean;
    onhoverCallback?: string;
}

interface onAction {
    action: string,
    displayName: string,
    icon: string,
    type: string,
}

export interface NavBarItemsProps {
    navbarItems: NavBarItemProps[],
    onNavigate: (url: string) => void,
    dynamicNavItemsWidth: number,
    loadMoreOptions?: (loadmoreArgs: LoadMoreArgsProps) => Promise<LoadMoreOptReturnProps>,
    pathName?: string,
    onAction?: (props: onAction) => void,
    onHover?: (props: NavBarItemProps) => void,
    navigationHooks?: any,
}

interface ExtendedLoadMoreArgsProps extends LoadMoreArgsProps {
    itemId: keyof typeof API_MAP;
}


const computeShouldOpenLeft = (el: HTMLElement | null, dropdownWidth: number): boolean => {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    return (window.innerWidth - rect.right - 10) < dropdownWidth;
};

const NavBarItems = ({ navbarItems, onNavigate, dynamicNavItemsWidth, loadMoreOptions, pathName, onAction, onHover, navigationHooks }: NavBarItemsProps): JSX.Element => {
    const [visibleItems, setVisibleItems] = useState<NavBarItemProps[]>(navbarItems);
    const [truncateItems, setTruncateItems] = useState<NavBarItemProps[]>([]);
    const navVisibleItemsContainerRef = useRef<HTMLDivElement>(null);
    const safePathName = pathName ?? '';
    // For AsyncSelct : 
    const [value, updateValue] = useState(null)
    const [numberOfRequests, setNumberOfRequests] = useState<number>(0)

    const [isOpen, setIsOpen] = useState(false);

    //To render all items, basically get a default view of all items
    const renderAllItems = () => {
        setVisibleItems(navbarItems);
        setTruncateItems([]);
    }

    const buildHotKeyMap = useMemo(() => {
        const map = new Map<string, NavBarItemProps>();
        navbarItems.forEach(item => {
            if (item?.hotKey) {
                map.set(item.hotKey.toLowerCase(), item);
            }
        });
        return map;
    }, [navbarItems]
    );

    const handleKeyDown = async (e: KeyboardEvent) => {
        if (!(isMac() ? e.metaKey : e.ctrlKey)) {
            return;
        }

        let hotkey = '';
        hotkey += e.key.toLowerCase();

        const hotKeyMap = buildHotKeyMap;

        if (hotKeyMap.has(hotkey)) {
            e.preventDefault();
            const item = hotKeyMap.get(hotkey);

            const isActive = item.id && safePathName && (item.id === safePathName || (item.activeStates && item.activeStates.includes(safePathName)));

            if (item.redirectUrl) {
                if (item.hookId) {
                    const { preNavigationHook, postNavigationHook } = navigationHooks[item.hookId];
                    if (preNavigationHook) {
                        await preNavigationHook();
                    }
                    if (!(item.preventRedirectIfActive && isActive)) {
                        onNavigate(item.redirectUrl);
                    }
                    if (postNavigationHook) {
                        await postNavigationHook();
                    }
                } else {
                    if (!(item.preventRedirectIfActive && isActive)) {
                        onNavigate(item.redirectUrl);
                    }
                }
            } else if (item.type === DropdownItemType.ACTION && onAction) {
                onAction(item as onAction);
            }
        }
    };

    useEffect(() => {
        //Get the default view of all items
        renderAllItems();

        // Calculate the visibility of items on window resize by observing the container
        const observer = new ResizeObserver(() => {
            const navbarItemsLength = navbarItems.length;
            if (navVisibleItemsContainerRef.current.children.length === navbarItemsLength) {
                calculateDynamicItemVisibility();
            } else {
            }

        })

        // Observe the container for changes to its child elements
        observer.observe(navVisibleItemsContainerRef?.current);

        // Add keydown event listener for hotkeys
        window.addEventListener('keydown', handleKeyDown);

        // Cleanup function: clear the observer and event listener on unmount
        return () => {
            observer.disconnect();
            window.removeEventListener('keydown', handleKeyDown);
        };

    }, [dynamicNavItemsWidth, navbarItems, navVisibleItemsContainerRef]);

    // Calculate the visibility of items based on the width of the dynamicNavItems
    const calculateDynamicItemVisibility = () => {
        // If the ref is not set, return
        if (!navVisibleItemsContainerRef.current) return;

        //Declare variables
        let cumulativeWidth = 0;
        let visibleCount = 0;
        const truncateButtonWidth = 72 // Width of the truncate button

        // Calculate the width of the dynamicNavItems container and the sum of the visible children
        const dynamicNavItemsWidthRounded = Math.round(dynamicNavItemsWidth)
        const visibleChildrenWidths: number[] = Array.from(navVisibleItemsContainerRef?.current.children).map(
            (child) => (child as HTMLElement).offsetWidth
        );

        // Calculate the available width for visible items (leave space for the truncation button)
        const availableWidth = dynamicNavItemsWidthRounded - truncateButtonWidth;

        // Calculate how many items fit within availableWidth
        for (let i = 0; i < visibleChildrenWidths?.length; i++) {
            if (cumulativeWidth + visibleChildrenWidths[i] <= availableWidth) {
                cumulativeWidth += visibleChildrenWidths[i];
                visibleCount++;
            } else {
                break;
            }
        }

        // Split items into visible and truncated items
        const visibleItems = navbarItems?.slice(0, visibleCount);
        const truncateItems = navbarItems?.slice(visibleCount);

        // Update state with visible and truncated items
        setVisibleItems(visibleItems);
        setTruncateItems(truncateItems);
    };

    return (
        <BrowserRouter>
            <div
                className="TopNavbar__content__items"
                id='navbar-items-wrapper-id'
            >
                {/* Render visible items */}
                <div
                    ref={navVisibleItemsContainerRef}
                    className="TopNavbar__content__items__list flex-v-center"
                    id='cs-visible-item-container-id'
                    key={navbarItems.length}
                >
                    {visibleItems.map((item: any) => {
                        switch (item.type) {
                            case DropdownItemType.REDIRECT:
                                return NavbarItem(item, onNavigate, safePathName, navigationHooks, onHover);
                            case DropdownItemType.DROPDOWN:
                                return <NavbarDropdown
                                    key={item.id || item.displayName}
                                    menuItem={item}
                                    onNavigate={onNavigate}
                                    safePathName={safePathName}
                                    onAction={onAction}
                                    onHover={onHover}
                                    value={value}
                                    updateValue={updateValue}
                                    numberOfRequests={numberOfRequests}
                                    setNumberOfRequests={setNumberOfRequests}
                                    loadMoreOptions={loadMoreOptions}
                                    navigationHooks={navigationHooks}
                                />;
                            case DropdownItemType.ASYNC_DROPDOWN:
                                return <NavbarAsyncDropdown
                                    menuItem={item}
                                    onNavigate={onNavigate}
                                    value={value}
                                    updateValue={updateValue}
                                    numberOfRequests={numberOfRequests}
                                    setNumberOfRequests={setNumberOfRequests}
                                    loadMoreOptions={loadMoreOptions}
                                    item={item}
                                    onHover={onHover}
                                    navigationHooks={navigationHooks}
                                    pathName={safePathName}
                                />;
                            default:
                                return null;
                        }
                    })}
                </div>
                {/* Render More (truncate) dropdown if there are hidden items */}
                {dynamicNavItemsWidth ? (truncateItems.length > 0 && <NavbarTruncateDropdown
                    menuItem={truncateItems}
                    onNavigate={onNavigate}
                    visibleItems={visibleItems.length}
                    onAction={onAction}
                    value={value}
                    updateValue={updateValue}
                    numberOfRequests={numberOfRequests}
                    setNumberOfRequests={setNumberOfRequests}
                    loadMoreOptions={loadMoreOptions}
                    navigationHooks={navigationHooks}
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                    safePathName={safePathName}
                    onHover={onHover}
                />) : null}
            </div>
        </BrowserRouter>
    );
};

const redirectButtonIconProps = {
    size: 'small',
    style: {
        marginRight: '0.25rem'
    },
    version: 'v2'
}

const redirectMenuButtonIconProps = (visibleItemsCountHelper: boolean) => {
    const styleUpdate = { ...redirectButtonIconProps.style };
    if (visibleItemsCountHelper) {
        styleUpdate.marginRight = '0';
        return {
            ...redirectButtonIconProps,
            style: styleUpdate
        };
    } else {
        return redirectButtonIconProps;
    }
}

const NavbarItem = (item: NavBarItemProps, onNavigate: (url: string) => void, safePathName: string, navigationHooks?: any, onHover?: (props: NavBarItemProps) => void): JSX.Element => {

    const isActive = item.id && safePathName && (item.id === safePathName || (item.activeStates && item.activeStates.includes(safePathName)));
    const isSelected = isActive ? '__isSelected' : '';

    const handleClick = async (e) => {
        e.currentTarget.blur();
        if ((isMac() && e.metaKey) || (!isMac() && e.ctrlKey)) {
            window.open(`/#!${item.redirectUrl}`, '_blank');
        } else if (onNavigate) {
            if (item.hookId) {
                const menuNavigationHook = navigationHooks[item.hookId];
                if (menuNavigationHook.preNavigationHook) {
                    await menuNavigationHook.preNavigationHook();
                }
                if (!(item.preventRedirectIfActive && isActive)) {
                    onNavigate(item.redirectUrl);
                }
                if (menuNavigationHook.postNavigationHook) {
                    await menuNavigationHook.postNavigationHook();
                }
            } else {
                if (!(item.preventRedirectIfActive && isActive)) {
                    onNavigate(item.redirectUrl);
                }
            }
        }
    };

    return (
        <div
            className='TopNavbar__content__items__list__redirect'
            onMouseEnter={(): void => { if (item.onhoverCallback) onHover?.(item); }}
        >
            <NavLink to={`/#!${item.redirectUrl}`} onClick={(e) => e.preventDefault()} tabIndex={-1}>
                <Button
                    className={`TopNavbar__content__items__list__redirect--button${isSelected}`}
                    version='v2'
                    aria-label={item.displayName}
                    iconProps={redirectButtonIconProps}
                    buttonType='tertiary'
                    size='small'
                    onClick={handleClick}
                    testId={item.testId}
                    tabIndex={0}
                    role="button"
                >
                    {item.notificationCount && !isActive && (
                        <div className="flex-v-center TopNavbar__content__items__list__redirect--button__icon-container">
                            <Icon icon={item.icon} version='v2' size="small" className="TopNavbar__Icon-margin-r4" />
                            <div className="TopNavbar__content__items__list__redirect--button__notification-count">
                                {item.notificationCount}
                            </div>
                        </div>
                    )}
                    {(!item.notificationCount || isActive) && <Icon icon={item.icon} version='v2' size="small" className="TopNavbar__Icon-margin-r4" />}
                    {item.displayName}
                </Button>
            </NavLink>
        </div>
    )
}

const NavbarDropdown: React.FC<{
    menuItem: NavBarItemProps;
    onNavigate: (url: string) => void;
    safePathName: string;
    onAction: (props: onAction) => void;
    onHover?: (props: NavBarItemProps) => void;
    value: any;
    updateValue: (data: any) => void;
    numberOfRequests: number;
    setNumberOfRequests: (count: number) => void;
    loadMoreOptions: (loadmoreArgs: ExtendedLoadMoreArgsProps) => Promise<LoadMoreOptReturnProps>;
    navigationHooks?: any;
}> = ({
    menuItem,
    onNavigate,
    safePathName,
    onAction,
    onHover,
    value,
    updateValue,
    numberOfRequests,
    setNumberOfRequests,
    loadMoreOptions,
    navigationHooks,
}): JSX.Element => {

    const dropdownRef = useRef<HTMLDivElement>(null);
    const [shouldOpenLeft, setShouldOpenLeft] = useState(false);

    const [asyncMenuOpen, setAsyncMenuOpen] = useState(false);

    useEffect(() => {
        let resizeRafId: number;
        
        const calculateDropdownPosition = () => {
            if (dropdownRef.current) {
                const rect = dropdownRef.current.getBoundingClientRect();
                const viewportWidth = window.innerWidth;
                const dropdownWidth = 300;
                const availableSpaceRight = (viewportWidth - rect.right) - 10;

                setShouldOpenLeft(availableSpaceRight < dropdownWidth);
            }
        };

        const rafId = requestAnimationFrame(() => {
            calculateDropdownPosition();
        });

        const handleResize = () => {
            cancelAnimationFrame(resizeRafId);
            resizeRafId = requestAnimationFrame(calculateDropdownPosition);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            cancelAnimationFrame(rafId);
            cancelAnimationFrame(resizeRafId);
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const isActive = menuItem.id && safePathName && (menuItem.id === safePathName || (menuItem.activeStates && menuItem.activeStates.includes(safePathName)));
    const isSelected = isActive ? '__isSelected' : '';

    const handleRedirect = async (e: any) => {
        e.currentTarget.blur();

        if (menuItem.redirectUrl) {
            if ((isMac() && e.metaKey) || (!isMac() && e.ctrlKey)) {
                window.open(`/#!${menuItem.redirectUrl}`, '_blank');
            } else if (onNavigate) {
                if (menuItem.hookId) {
                    const menuNavigationHook = navigationHooks[menuItem.hookId];
                    if (menuNavigationHook.preNavigationHook) {
                        await menuNavigationHook.preNavigationHook(); 
                    }
                    if (!(menuItem.preventRedirectIfActive && isActive)) {
                        onNavigate(menuItem.redirectUrl);
                    }
                    if (menuNavigationHook.postNavigationHook) {
                        await menuNavigationHook.postNavigationHook();
                    }
                } else {
                    if (!(menuItem.preventRedirectIfActive && isActive)) {
                        onNavigate(menuItem.redirectUrl);
                    }
                }
            }
        }
    };

    return (
        <div
            ref={dropdownRef}
            className='TopNavbar__content__items__list__dropdown flex-v-center'
            onMouseEnter={() => {
                setShouldOpenLeft(computeShouldOpenLeft(dropdownRef.current, 300));
                if (menuItem.onhoverCallback) onHover?.(menuItem);
            }}
        >
            <Menu
                version='v2'
                type="hover"
                renderInPortal
                portalTarget={document.body}
                portalClassName="TopNavbar__portal-menu"
                menuButtonClickFunction={handleRedirect}
                list={
                    menuItem.subMenu?.map(item => {
                        const subMenuItem: IMenu = {
                            label: item?.displayName,
                            testId: item?.testId,
                            key: item?.displayName.toLowerCase().replace(" ", "-"),
                            redirectUrl: item.redirectUrl && `/#!${item.redirectUrl}`,
                            action: async (): Promise<void> => {
                                if (item.type === DropdownItemType.ACTION) {
                                    onAction(item as any)
                                } else if (item.type === DropdownItemType.REDIRECT) {
                                    if (item.hookId) {
                                        const menuNavigationHook = navigationHooks[item.hookId];
                                        if (menuNavigationHook.preNavigationHook) {
                                            await menuNavigationHook.preNavigationHook();
                                        }
                                        onNavigate(item.redirectUrl);
                                        if (menuNavigationHook.postNavigationHook) {
                                            await menuNavigationHook.postNavigationHook();
                                        }
                                    } else {
                                        onNavigate(item.redirectUrl);
                                    }
                                }
                            }
                        }
                        if (item.subMenu) {
                            subMenuItem.subList = item.subMenu.map(subItem => ({
                                label: subItem?.displayName,
                                action: (): void => {
                                    if (subItem.type === DropdownItemType.ACTION) {
                                        onAction(subItem as onAction)
                                    } else if (subItem.type === DropdownItemType.REDIRECT) {
                                        onNavigate(subItem.redirectUrl)
                                    }
                                },
                                redirectUrl: subItem.redirectUrl && `/#!${subItem.redirectUrl}`,
                                data: { width: 400, height: 800 },
                                testId: subItem?.testId,
                                key: subItem?.displayName.toLowerCase().replace(" ", "-"),
                            }))
                            subMenuItem.postFixComponent = (index: number, item: IMenu, isOpen: boolean): JSX.Element => (
                                <Icon icon={(isOpen && shouldOpenLeft) ? "CaretLeft" : "CaretRight"} version='v2' className="rightArrow" size="small" />);
                        }

                        if (item.type === DropdownItemType.ACTION) {
                            subMenuItem.action = (): void => onAction(item as onAction);
                            subMenuItem.data = { width: 400, height: 800 };
                        }

                        if (item.type === DropdownItemType.ASYNC_DROPDOWN) {
                            subMenuItem.label = (
                                <NavbarAsyncDropdown
                                    menuItem={item}
                                    onNavigate={onNavigate}
                                    value={value}
                                    updateValue={updateValue}
                                    numberOfRequests={numberOfRequests}
                                    setNumberOfRequests={setNumberOfRequests}
                                    loadMoreOptions={loadMoreOptions}
                                    isPartOfMenu={true}
                                    item={item}
                                    menuIsOpen={asyncMenuOpen}
                                />
                            ) as ReactNode;
                            subMenuItem.postFixComponent = (index: number, menuItem: IMenu, isOpen: boolean): JSX.Element => (
                                <Icon icon={(isOpen && shouldOpenLeft) ? "CaretLeft" : "CaretRight"} version='v2' className="rightArrow" size="small" />);
                            subMenuItem.accFun = (state: boolean): void => setAsyncMenuOpen(state);
                            subMenuItem.accState = asyncMenuOpen;
                            subMenuItem.subList = [];
                        }
                        return subMenuItem;
                    }) ?? []}
                subMenuPosition={shouldOpenLeft ? 'left' : 'right'}
            >
                <NavLink to={`/#!${menuItem.redirectUrl}`} onClick={(e) => e.preventDefault()} tabIndex={-1}>
                    <Button
                        version='v2'
                        className={`${menuItem.id} TopNavbar__content__items__list__dropdown--button${isSelected}`}
                        iconProps={redirectButtonIconProps}
                        testId={menuItem.testId}
                        buttonType='tertiary'
                        size='small'
                        // onClick={handleRedirect}
                        role="button"
                        aria-label={menuItem.displayName}
                        tabIndex={-1}
                    >
                        {menuItem.notificationCount && !isActive && (
                            <div className="TopNavbar__content__items__list__dropdown--button__icon-container">
                                <Icon icon={menuItem.icon} version='v2' size="small" className="TopNavbar__Icon-margin-r4" />
                                <div className="TopNavbar__content__items__list__dropdown--button__notification-count">
                                    {menuItem.notificationCount}
                                </div>
                            </div>
                        )}
                        {(!menuItem.notificationCount || isActive) && <Icon icon={menuItem.icon} version='v2' size="small" className="TopNavbar__Icon-margin-r4" />}
                        {menuItem.displayName}
                        <Icon
                            className={`${menuItem.id} TopNavbar__content__items__list__dropdown--button${isSelected}--Icon`}
                            version="v2"
                            icon="CaretDownNew"
                            height="16px"
                            width="16px"
                            size='tiny'
                        />
                    </Button>
                </NavLink>
            </Menu>
        </div >

    )
}

const NavbarTruncateDropdown: React.FC<{
    menuItem: NavBarItemProps[];
    onNavigate: (url: string) => void;
    visibleItems: number;
    onAction?: (props: onAction) => void;
    value?: any;
    updateValue?: (data: any) => void;
    numberOfRequests?: number;
    setNumberOfRequests?: (count: number) => void;
    loadMoreOptions?: (loadmoreArgs: ExtendedLoadMoreArgsProps) => Promise<LoadMoreOptReturnProps>;
    navigationHooks?: any;
    isOpen?: boolean;
    setIsOpen?: (isOpen: boolean) => void;
    safePathName?: string;
    onHover?: (props: NavBarItemProps) => void;
}> = ({
    menuItem,
    onNavigate,
    visibleItems,
    onAction,
    value,
    updateValue,
    numberOfRequests,
    setNumberOfRequests,
    loadMoreOptions,
    navigationHooks,
    isOpen,
    setIsOpen,
    safePathName = '',
    onHover,
}): JSX.Element => {

    const dropdownRef = useRef<HTMLDivElement>(null);
    const [shouldOpenLeft, setShouldOpenLeft] = useState(false);

    // Detect viewport space and determine if dropdown should open on left side
    useEffect(() => {
        let resizeRafId: number;
        
        const calculateDropdownPosition = () => {
            if (dropdownRef.current) {
                const rect = dropdownRef.current.getBoundingClientRect();
                const viewportWidth = window.innerWidth;
                const dropdownWidth = 300; // Approximate width of the menu dropdown
                const availableSpaceRight = (viewportWidth - rect.right) - 10;

                setShouldOpenLeft(availableSpaceRight < dropdownWidth);
            }
        };

        const rafId = requestAnimationFrame(() => {
            calculateDropdownPosition();
        });

        const handleResize = () => {
            cancelAnimationFrame(resizeRafId);
            resizeRafId = requestAnimationFrame(calculateDropdownPosition);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            cancelAnimationFrame(rafId);
            cancelAnimationFrame(resizeRafId);
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const visibleItemsCountHelper = false;

    const truncatedItems: IMenu[] = menuItem.map((item) => {

        // const navItemSubList = item.subMenu?.map((subItem) => ({
        //     label: subItem.displayName,
        //     action: (): void => subItem.redirectUrl ? onNavigate(subItem.redirectUrl) : null,
        //     postFixComponent: subItem.subMenu ? () => <Icon icon="Dashboard" version='v2' size="small" /> : null,
        //     testId: subItem.testId,
        //     key: subItem.displayName.toLowerCase().replace(" ", "-")
        // }));

        const isActive = item.id && safePathName && (item.id === safePathName || (item.activeStates && item.activeStates.includes(safePathName)));

        const subMenuNavItem: IMenu = {
            label: (
                <span className={`TopNavbar__content__items__truncate__item${isActive ? ' TopNavbar__content__items__truncate__item--active' : ''}`}>
                    {item.notificationCount && (
                        <div className="flex-v-center TopNavbar__content__items__list__redirect--button__icon-container">
                            <Icon icon={item.icon} version='v2' size="small" className="TopNavbar__Icon-margin-r4" />
                            <div className="TopNavbar__content__items__list__redirect--button__notification-count">
                                {item.notificationCount}
                            </div>
                        </div>
                    )}
                    {!item.notificationCount && <Icon icon={item.icon} version='v2' size="small" className="TopNavbar__Icon-margin-r4" />}
                    {item.displayName}
                </span>
            ),
            action: (): void => {
                if (item.redirectUrl && !(item.preventRedirectIfActive && isActive)) {
                    onNavigate(item.redirectUrl);
                }
            },
            subList: null,
            redirectUrl: item.redirectUrl && `/#!${item.redirectUrl}`,
            onMouseEnter: (): void => { if (item.onhoverCallback) onHover?.(item); },
            testId: item.testId,
            key: item.displayName.toLowerCase().replace(" ", "-")
        }

        if (item.type === DropdownItemType.ASYNC_DROPDOWN) {
            subMenuNavItem.label = (
                <NavbarAsyncDropdown
                    menuItem={item}
                    onNavigate={onNavigate}
                    value={value}
                    updateValue={updateValue}
                    numberOfRequests={numberOfRequests}
                    setNumberOfRequests={setNumberOfRequests}
                    loadMoreOptions={loadMoreOptions}
                    isPartOfMenu={true}
                    isTruncated={true}
                    item={item}
                    menuIsOpen={isOpen}
                />
            ) as ReactNode,
                subMenuNavItem.postFixComponent = (index: number, menuItem: IMenu, isMenuOpen: boolean) => (
                    <Icon icon={(isMenuOpen && shouldOpenLeft) ? "CaretLeft" : "CaretRight"} version='v2' className="rightArrow" size="small" style={{ zIndex: '1' }} />
                ),
                subMenuNavItem.accFun = (state: boolean) => {
                    setIsOpen(state);
                 }
                 subMenuNavItem.accState = isOpen;
                subMenuNavItem.subList = [];
        }
        return subMenuNavItem;

    });

    return (
        <div
            ref={dropdownRef}
            className='TopNavbar__content__items__truncate__wrapper'
            onMouseEnter={() => setShouldOpenLeft(computeShouldOpenLeft(dropdownRef.current, 300))}
        >
            <Menu
                version="v2"
                type="hover"
                renderInPortal
                portalTarget={document.body}
                portalClassName="TopNavbar__portal-menu TopNavbar__portal-menu--truncate"
                list={truncatedItems}
                subMenuPosition={shouldOpenLeft ? 'left' : 'right'}
            >
                <Button
                    version="v2"
                    className={`TopNavbar__content__items__truncate__wrapper__button`}
                    icon="SeeMore"
                    iconProps={redirectMenuButtonIconProps(visibleItemsCountHelper)}
                    testId={"cs-dropdown-truncate-button"}
                    buttonType="tertiary"
                    size="small"
                    tabIndex={-1}
                >
                    {(visibleItemsCountHelper) ? "" : "More"}
                </Button>
            </Menu>
        </div>)
}

const NavbarAsyncDropdown: React.FC<{
    menuItem: NavBarItemProps;
    onNavigate: (url: string) => void;
    value: any;
    updateValue: (data: any) => void;
    numberOfRequests: number;
    setNumberOfRequests: (count: number) => void;
    loadMoreOptions: (loadmoreArgs: ExtendedLoadMoreArgsProps) => Promise<LoadMoreOptReturnProps>;
    isPartOfMenu?: boolean;
    isTruncated?: boolean;
    item: any;
    onHover?: (props: NavBarItemProps) => void;
    navigationHooks?: any;
    menuIsOpen?: boolean;
    pathName?: string;
}> = ({
    menuItem,
    onNavigate,
    value,
    updateValue,
    numberOfRequests,
    setNumberOfRequests,
    loadMoreOptions,
    isPartOfMenu = false,
    isTruncated = false,
    item,
    onHover,
    navigationHooks,
    menuIsOpen,
    pathName,
}): JSX.Element => {
        const isPartOfMenuClass = isPartOfMenu ? '--partOfMenu' : '';
        const dropdownRef = useRef<HTMLDivElement>(null);
        const [shouldOpenLeft, setShouldOpenLeft] = useState(false);
        const [asyncMenuIsOpen, setAsyncMenuIsOpen] = useState(false);

        // Detect viewport space and determine if dropdown should open on left side
        useEffect(() => {
            let resizeRafId: number;
            
            const calculateDropdownPosition = () => {
                if (dropdownRef.current && (isTruncated || isPartOfMenu)) {
                    const rect = dropdownRef.current.getBoundingClientRect();
                    const viewportWidth = window.innerWidth;
                    const dropdownWidth = 200;
                    const availableSpaceRight = (viewportWidth - rect.right) - 10;

                    setShouldOpenLeft(availableSpaceRight < dropdownWidth);
                }
            };

            const rafId = requestAnimationFrame(() => {
                calculateDropdownPosition();
            });

            const handleResize = () => {
                cancelAnimationFrame(resizeRafId);
                resizeRafId = requestAnimationFrame(calculateDropdownPosition);
            };

            window.addEventListener('resize', handleResize);
            return () => {
                cancelAnimationFrame(rafId);
                cancelAnimationFrame(resizeRafId);
                window.removeEventListener('resize', handleResize);
            };
        }, [isTruncated, isPartOfMenu, menuIsOpen]);

        const safePathName = pathName ?? '';
        const isActive = menuItem.id && safePathName && (menuItem.id === safePathName || (menuItem.activeStates && menuItem.activeStates.includes(safePathName)));

        const handleRedirect = async (e) => {
            if (menuItem.redirectUrl) {
                if ((isMac() && e.metaKey) || (!isMac() && e.ctrlKey)) {
                    window.open(`/#!${item.redirectUrl}`, '_blank');
                } else if (onNavigate) {
                    if (menuItem.hookId) {
                        const menuNavigationHook = navigationHooks[menuItem.hookId];
                        if (menuNavigationHook.preNavigationHook) {
                            await menuNavigationHook.preNavigationHook();
                        }
                        if (!(menuItem.preventRedirectIfActive && isActive)) {
                            onNavigate(menuItem.redirectUrl);
                        }
                        if (menuNavigationHook.postNavigationHook) {
                            await menuNavigationHook.postNavigationHook();
                        }
                    } else {
                        if (!(menuItem.preventRedirectIfActive && isActive)) {
                            onNavigate(menuItem.redirectUrl);
                        }
                    }
                }
            }
        };

        let pageSize = 10;

        const handleValueUpdate = (data: any) => {
            data.value();
        };

        const handleMenuOpen = () => {
            setAsyncMenuIsOpen(true);
        };

        const handleMenuClose = () => {
            setAsyncMenuIsOpen(false);
        };

        return (
            <div
                ref={dropdownRef}
                className={`TopNavbar__content__items__list__dropdown TopNavbar__content__items__list__async__dropdown${isPartOfMenuClass} flex-v-center`}
                onMouseEnter={() => {
                    if (isTruncated || isPartOfMenu) setShouldOpenLeft(computeShouldOpenLeft(dropdownRef.current, 200));
                    if (menuItem.onhoverCallback) onHover?.(menuItem);
                }}
            >
                <AsyncSelect
                    classNamePrefix="TopNavbarAsyncSelect"
                    value={value}
                    onChange={handleValueUpdate}
                    openOnHover={true}
                    hasIcon={true}
                    key={menuIsOpen ? "open" : "close"}
                    placeholder={(
                        <>
                            <Button
                                version='v2'
                                className={`${menuItem.id} TopNavbar__content__items__list__async__dropdown__button${isPartOfMenuClass}`}
                                icon={menuItem.icon}
                                iconProps={{...redirectButtonIconProps, size: 'medium'}}
                                testId={menuItem.testId}
                                buttonType='tertiary'
                                size='small'
                                onClick={handleRedirect}
                                aria-label={menuItem.displayName}
                            >
                                {menuItem.displayName}
                                {!isPartOfMenu &&
                                    <Icon
                                        className={`TopNavbar__content__items__list__async__dropdown__button--Icon${isPartOfMenuClass}`}
                                        version="v2"
                                        icon={asyncMenuIsOpen && shouldOpenLeft ? "CaretLeft" : "CaretDownNew"}
                                        height="16px"
                                        width="16px"
                                        size='tiny'
                                    />
                                }
                            </Button>
                        </>
                    ) as unknown as string}
                    loadMoreOptions={(params) => loadMoreOptions({ ...params, itemId: item.id })}
                    limit={pageSize}
                    truncateLabel={true}
                    width="200px"
                    isClearable
                    defaultOptions={false}
                    debounceTimeout={0}
                    version='v2'
                    containsLink={true}
                    styles={{
                        menuPortal: (base: any): any => ({
                            ...base,
                            zIndex: 9999,
                            marginTop: '-0.563rem',
                            marginLeft: shouldOpenLeft ? '0.063rem' : '0rem',
                        }),
                        menu: (base: any) => ({
                            ...base,
                            left: (() => {
                                if (isTruncated) {
                                    return shouldOpenLeft ? '-0.2rem' : '14.02rem';
                                } else if (isPartOfMenu) {
                                    return shouldOpenLeft ? '0' : '14.313rem';
                                }
                                return 'initial';
                            })(),
                            transform: (() => {
                                if (isTruncated && shouldOpenLeft) {
                                    return 'translateX(-100%)';
                                } else if (isPartOfMenu && shouldOpenLeft) {
                                    return 'translateX(-100%)';
                                }
                                return 'none';
                            })(),
                            top: isPartOfMenu ? shouldOpenLeft ? '-2.5rem' : '-2.25rem' : 'initial',
                            width: 'initial',
                            boxShadow: "0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 3px rgba(0, 0, 0, 0.12), 0px 4px 15px 0px rgba(108, 92, 231, 0.20) !important",
                            padding: '0.5rem 0rem',
                            '[class$="-MenuList"]': {
                                maxHeight: "19.313rem !important",
                            },
                            marginLeft: '0 !important',
                        }),
                        menuList: (base) => ({
                            ...base,
                            "scrollbarWidth": "none", 
                            "msOverflowStyle": "none",
                            "&::-webkit-scrollbar": {
                                display: "none",
                            },
                            "& .Portal__option": {

                                color: "#212121",
                                "fontSize": ".875rem !important",
                                "height": "2.5rem !important",
                                "display": "flex",
                                "alignItems": "center",
                                "textDecoration": "none",
                                "width": "200px",
                                "maxWidth": "200px",
                                "minWidth": "200px",
                                "overflow": "hidden",

                                "> a": {
                                    "textDecoration": "none",
                                    "color": "inherit",
                                    "width": "100%",
                                },

                                "> div, > a > div": {
                                    "width": "100%",
                                },
                                "&:hover": {
                                    background: "#6c5ce70d !important",
                                    color: "#5a20b9 !important",
                                },
                                "&--is-focused": {
                                    backgroundColor: "transparent !important",
                                    color: "inherit !important",
                                },
                                "& .tippy-wrapper": {
                                    width: "100% !important",
                                },
                            },
                        }),

                    }}
                    menuPortalTarget={document.body}
                    tabSelectsValue={false}
                    menuIsOpen={menuIsOpen}
                    onMenuOpen={handleMenuOpen}
                    onMenuClose={handleMenuClose}
                />
            </div>
        );
    };

export default NavBarItems;